import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  notification,
  Space,
  Skeleton,
  Tag,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import {
  useDeleteSubcontractorMutation,
  useSubcontractorMemberOptions,
  useSubcontractors,
  useUpsertSubcontractorMutation,
} from "subcontractors/queries";
import { ISubcontractorMember, ISimpleSubcontractor } from "subcontractors/models";
import { useLookupOptions } from "admin/queries";
import "./SubcontractorSetup.scss";

interface SubcontractorWithMembers extends ISimpleSubcontractor {
  memberDetails?: ISubcontractorMember[];
}

export const SubcontractorSetup = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubcontractor, setEditingSubcontractor] = useState<SubcontractorWithMembers | null>(null);
  const [memberOptionsSubcontractorId, setMemberOptionsSubcontractorId] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [api, notificationContextHolder] = notification.useNotification();
  const [subcontractorsWithMembers, setSubcontractorsWithMembers] = useState<SubcontractorWithMembers[]>([]);

  // Fetch lookup options from API
  const { data: projectTypeOptions = [] } = useLookupOptions("project_type");

  const {
    data: subcontractorsData,
    isLoading: subcontractorsLoading,
    isSuccess: subcontractorsSuccess,
  } = useSubcontractors();

  const {
    data: memberSelectOptions,
    isLoading: memberOptionsLoading,
  } = useSubcontractorMemberOptions(memberOptionsSubcontractorId);

  const upsertMutation = useUpsertSubcontractorMutation();
  const deleteMutation = useDeleteSubcontractorMutation();

  // Update state when subcontractors data changes
  useEffect(() => {
    if (subcontractorsData && subcontractorsData.length > 0) {
      setSubcontractorsWithMembers(subcontractorsData.map(s => ({ ...s })));
    } else {
      setSubcontractorsWithMembers([]);
    }
  }, [subcontractorsData]);

  const handleOpenModal = useCallback((subcontractor?: SubcontractorWithMembers) => {
    if (subcontractor) {
      setEditingSubcontractor(subcontractor);
      form.setFieldsValue({
        name: subcontractor.name,
        type: subcontractor.type,
        members: subcontractor.members || [],
      });
      // Set the subcontractor id so the member options query fetches for this subcontractor
      setMemberOptionsSubcontractorId(subcontractor.id);
    } else {
      setEditingSubcontractor(null);
      form.resetFields();
      // Clear the subcontractor id for new subcontractor (no exclusions)
      setMemberOptionsSubcontractorId(undefined);
    }
    setIsModalOpen(true);
  }, [form]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingSubcontractor(null);
    form.resetFields();
  }, [form]);

  const handleSave = async (values: { name: string; type: string; members: string[] }) => {
    try {
      await upsertMutation.mutateAsync({
        id: editingSubcontractor?.id,
        name: values.name,
        type: values.type,
        members: values.members || [],
      } as ISimpleSubcontractor);
      api.success({
        duration: 3,
        message: editingSubcontractor ? "Subcontractor Updated" : "Subcontractor Created",
        description: `The subcontractor has been ${editingSubcontractor ? "updated" : "created"} successfully.`,
      });
      setIsModalOpen(false);
      setEditingSubcontractor(null);
      form.resetFields();
    } catch (error) {
      api.error({
        message: "Error",
        description: "Failed to save subcontractor. Please try again.",
      });
    }
  };

  const handleDelete = (subcontractor: SubcontractorWithMembers) => {
    modal.confirm({
      title: "Delete Subcontractor",
      content: `Are you sure you want to delete "${subcontractor.name}"? This cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      icon: <ExclamationCircleOutlined />,
      cancelText: "Cancel",
      onOk: async () => {
        if (subcontractor.id) {
          try {
            await deleteMutation.mutateAsync(subcontractor.id);
            api.success({
              duration: 3,
              message: "Subcontractor Deleted",
              description: "The subcontractor has been deleted successfully.",
            });
          } catch (error) {
            api.error({
              message: "Error",
              description: "Failed to delete subcontractor. Please try again.",
            });
          }
        }
      },
    });
  };

  const getTypeLabel = (type?: string) => {
    return projectTypeOptions.find((t) => t.value === type)?.label || type || "\u2014";
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case "hvac":
        return "blue";
      case "lighting":
        return "gold";
      case "refrigeration":
        return "cyan";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: SubcontractorWithMembers, b: SubcontractorWithMembers) =>
        (a.name ?? "").localeCompare(b.name ?? ""),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{getTypeLabel(type)}</Tag>
      ),
    },
    {
      title: "Members",
      dataIndex: "members",
      key: "members",
      render: (members: string[] | undefined) =>
        members && members.length > 0
          ? `${members.length} member${members.length > 1 ? "s" : ""}`
          : "\u2014",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: unknown, record: SubcontractorWithMembers) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            Edit
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (!subcontractorsSuccess && subcontractorsLoading) {
    return <Skeleton active />;
  }

  return (
    <div className="subcontractor-setup" data-testid="subcontractorsetup">
      {notificationContextHolder}
      {contextHolder}

      <div className="subcontractor-setup-header">
        <Typography.Title className="subcontractor-setup-title">
          Subcontractor Setup
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          Add Subcontractor
        </Button>
      </div>

      <Typography.Paragraph>
        Manage subcontractors and their team members. Each subcontractor can be assigned a type
        (HVAC, Lighting, or Refrigeration) and have multiple members.
      </Typography.Paragraph>

      <Table
        columns={columns}
        dataSource={subcontractorsWithMembers}
        loading={subcontractorsLoading}
        rowKey="id"
        pagination={false}
        size="middle"
        className="subcontractor-setup-table"
      />

      <Modal
        title={editingSubcontractor ? "Edit Subcontractor" : "Add Subcontractor"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="Subcontractor Name"
            rules={[{ required: true, message: "Subcontractor name is required" }]}
          >
            <Input placeholder="Enter subcontractor name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Subcontractor Type"
            rules={[{ required: true, message: "Subcontractor type is required" }]}
          >
            <Select
              placeholder="Select type"
              options={projectTypeOptions}
            />
          </Form.Item>

          <Form.Item name="members" label="Team Members">
            <Select
              mode="multiple"
              placeholder="Select team members"
              loading={memberOptionsLoading}
              options={memberSelectOptions?.map((opt) => ({
                value: opt.value,
                label: opt.label,
              })) || []}
              allowClear
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={upsertMutation.isPending}
              >
                {editingSubcontractor ? "Update" : "Create"}
              </Button>
              <Button onClick={handleCloseModal}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
